import {Reason} from '../reason.js';
import {DomainMessage, SendMessageAction} from '../node.js';
import {DomainUri} from '../state/types/domain-registry.js';
import {ErrorMessage, PeerAdded, PeerMessage, PeerRemoved, RequestMessage, SuccessMessage} from './types/messages.js';
import {Identity, PeerId, Source} from '../state/types/common.js';
import {IOGateway} from '../../gateway';

export type TokenMessage = IOGateway.Message<'token'> & DomainMessage & PeerMessage & RequestMessage & { token: string }

export function outgoing<T>(recipient: Source, message: IOGateway.Message<T>): SendMessageAction<T> {
    return {receiver: recipient, body: message};
}


export function broadcast<T>(source: Source, message: IOGateway.Message<T>): SendMessageAction<T> & { source: Source } {
    return {...outgoing({type: 'cluster'}, message), source}
}

export function unicast<T>(source: Source, recipient: Source, message: IOGateway.Message<T>): SendMessageAction<T> & { source: Source } {
    return {...outgoing(recipient, message), source};
}

function errorMsg(domainUri: DomainUri | undefined, requestId: string | undefined, peerId: string | undefined, reason: Reason, context?): ErrorMessage {
    const error: ErrorMessage = {
        type: 'error',
        request_id: requestId,
        reason_uri: reason.uri,
        reason: reason.message
    };
    if (domainUri) {
        error.domain = domainUri;
    }
    if (peerId) {
        error.peer_id = peerId;
    }
    if (context) {
        error.context = context;
    }
    return error;
}

export function error(domainUri: DomainUri | undefined, recipient: Source, requestId: string | undefined, peerId: PeerId | undefined, reason: Reason, context?): SendMessageAction<'error'> {
    return outgoing(recipient, errorMsg(domainUri, requestId, peerId, reason, context));
}

function successMsg(domainUri, requestId: string, peerId: string): SuccessMessage {
    return {type: 'success', request_id: requestId, domain: domainUri, peer_id: peerId};
}

export function success(domainUri: DomainUri, recipient: Source, requestId, peerId): SendMessageAction<'success'> {
    return outgoing(recipient, successMsg(domainUri, requestId, peerId))
}

export function isLocalSource(source: Source): boolean {
    const type = source.type;
    return type && type === 'local';
}

export function isRemoteSource(source: Source): boolean {
    return !isLocalSource(source);
}

function tokenMsg(domainUri: DomainUri, requestId: string, peerId: string, t: string): TokenMessage {
    return {domain: domainUri, type: 'token', request_id: requestId, peer_id: peerId, token: t};
}

export function token(domainUri: DomainUri, recipient: Source, requestId: string, peerId: PeerId, t: string): SendMessageAction<'token'> {
    return outgoing(recipient, tokenMsg(domainUri, requestId, peerId, t))
}

function peerAddedMsg(domainUri: DomainUri, peerId: PeerId, newPeerId: PeerId, newPeerIdentity: Identity, meta: {local: boolean}): PeerAdded {
    return {
        domain: domainUri,
        type: 'peer-added',
        peer_id: peerId,
        new_peer_id: newPeerId,
        identity: newPeerIdentity,
        meta
    };
}

export function peerAdded(domainUri: DomainUri, recipient: Source, peerId: PeerId, newPeerId: PeerId, newPeerIdentity: Identity, meta: {local: boolean}): SendMessageAction<'peer-added'> {
    return outgoing(recipient, peerAddedMsg(domainUri, peerId, newPeerId, newPeerIdentity, meta));
}

function peerRemovedMsg(domainUri: DomainUri, peerId: PeerId, removedPeerId: PeerId, reason: Reason): PeerRemoved {
    return {
        domain: domainUri,
        type: 'peer-removed',
        peer_id: peerId,
        removed_id: removedPeerId,
        reason_uri: reason.uri,
        reason: reason.message
    }
}

export function peerRemoved(domainUri: DomainUri, recipient, peerId, removedPeerId, reason: Reason): SendMessageAction<'peer-removed'> {
    return outgoing(recipient, peerRemovedMsg(domainUri, peerId, removedPeerId, reason))
}
